import { useState } from 'react';
import {
  Badge,
  BUTTON_CLASS,
  DataTable,
  DetailDrawer,
  formatUtcDateTime,
  MetaRow,
  neutralButtonStyle,
  Select,
  StatusDot,
  token,
  type DataTableColumn,
  type SelectOption,
} from '@bp-files/ui';
import { AUDIT_COPY } from '@/components/Audit/audit.copy';
import { useAudit, type AuditFilters } from '@/lib/useAudit';
import { useSpaces } from '@/lib/useSpaces';
import type { AuditEvent } from '@/lib/api';

/**
 * The Audit surface — one agency's chain-of-custody log (SPEC §8).
 *
 * **Read-only, and structurally so.** AUD-1 makes the log append-only, so there
 * are no row actions at all — no confirm, no refetch-after-mutation, none of the
 * machinery `MembersTab` and `RecycleBin` carry. The only interactive things
 * here read: the filters, the details drawer, and Load more.
 *
 * Four rules this encodes rather than decorates:
 *
 * - **The filters are server-side**, which is the exact inverse of
 *   `MembersTab`'s. Its comment says client-side filtering "is honest only
 *   because `GET /users` returns the whole list rather than a page" — this
 *   endpoint pages, so filtering here would filter one page and call it the log.
 *   `DataTable`'s sorting stays off for the same reason, turned one further:
 *   the order **is** the log's.
 * - **An empty page is not the end.** The server scans a bounded number of rows
 *   per request (ADR-0015), so a selective filter can match none of them and
 *   still have more to read. `hasMore` comes from the token, never from the row
 *   count, and the Load more control says so when a page comes back empty.
 * - **The space filter admits every space status**, unlike every other space picker
 *   in this workspace. AUD-1 makes a severed space's history permanent, and
 *   "what happened before we severed" is a question this screen exists to
 *   answer. Do not copy `MembersTab`'s `activeSpaces` filter here.
 * - **`space.opened` looks out of order, and it is not.** AUD-6a keys the row
 *   from the start of its UTC day while its timestamp is the day's first open,
 *   so it can sit below rows that are older. The marker and the drawer's
 *   sentence are the only places a reader can learn that.
 */

const EVENT_CLASS_OPTIONS: SelectOption[] = [
  { value: 'all', label: AUDIT_COPY.filters.eventClass.all },
  { value: 'agency', label: AUDIT_COPY.filters.eventClass.agency },
  { value: 'counterpart', label: AUDIT_COPY.filters.eventClass.counterpart },
];

/** Falls back to the raw action — a log renders a row it does not recognise. */
function actionLabel(action: string): string {
  return AUDIT_COPY.actions[action] ?? action;
}

export function AuditSurface() {
  const [filters, setFilters] = useState<AuditFilters>({ eventClass: 'all' });
  const [selected, setSelected] = useState<AuditEvent | undefined>(undefined);

  const { state, retry, loadMore } = useAudit(filters);
  const spaces = useSpaces();

  // The whole set, every status. See the note at the top of this file.
  const spaceOptions = spaces.state.kind === 'loaded' ? spaces.state.spaces : [];
  const spaceSelectOptions: SelectOption[] = [
    { value: '', label: AUDIT_COPY.filters.space.all },
    ...spaceOptions.map((space) => ({ value: space.id, label: space.name })),
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-4">
        <label
          className="flex items-center gap-2 text-xs"
          style={{ color: token('bp-text-muted') }}
        >
          {AUDIT_COPY.filters.eventClass.label}
          <Select
            label={AUDIT_COPY.filters.eventClass.label}
            value={filters.eventClass}
            onChange={(next) => {
              setFilters((current) => ({
                ...current,
                eventClass: next as AuditFilters['eventClass'],
              }));
            }}
            options={EVENT_CLASS_OPTIONS}
          />
        </label>

        <label
          className="flex items-center gap-2 text-xs"
          style={{ color: token('bp-text-muted') }}
        >
          {AUDIT_COPY.filters.space.label}
          <Select
            label={AUDIT_COPY.filters.space.label}
            value={filters.spaceId ?? ''}
            onChange={(spaceId) => {
              setFilters((current) => {
                const { spaceId: _dropped, ...rest } = current;
                return spaceId === '' ? rest : { ...rest, spaceId };
              });
            }}
            options={spaceSelectOptions}
          />
        </label>

        <ActorFilter
          applied={filters.actorEmail}
          events={state.kind === 'loaded' ? state.events : []}
          onApply={(email) => {
            setFilters((current) => {
              const { actorEmail: _dropped, ...rest } = current;
              return email === '' ? rest : { ...rest, actorEmail: email };
            });
          }}
        />
      </div>

      {state.kind === 'loading' ? (
        <p role="status" className="text-sm" style={{ color: token('bp-text-muted') }}>
          {AUDIT_COPY.states.loading}
        </p>
      ) : null}

      {state.kind === 'error' ? <AuditError status={state.status} onRetry={retry} /> : null}

      {state.kind === 'loaded' ? (
        <>
          <AuditTable
            events={state.events}
            onInspect={setSelected}
            onFilterActor={(email) => {
              setFilters((current) => ({ ...current, actorEmail: email }));
            }}
          />

          {state.hasMore ? (
            <div className="flex flex-col items-start gap-2">
              {state.events.length === 0 ? (
                // Nothing matched in what was scanned. A `status` and never an
                // `alert`: nothing failed, there is simply more log to read.
                <p role="status" className="text-xs" style={{ color: token('bp-text-muted') }}>
                  {AUDIT_COPY.loadMore.none}
                </p>
              ) : null}

              <button
                type="button"
                disabled={state.loadingMore}
                onClick={loadMore}
                className={BUTTON_CLASS}
                style={neutralButtonStyle()}
              >
                {state.loadingMore ? AUDIT_COPY.loadMore.working : AUDIT_COPY.loadMore.label}
              </button>
            </div>
          ) : null}
        </>
      ) : null}

      {selected === undefined ? null : (
        // Mounted only while open and keyed by the event id — `GrantEditor`'s
        // rule, so nothing from the last row survives into the next.
        <EventDetails
          key={selected.id}
          event={selected}
          onClose={() => {
            setSelected(undefined);
          }}
        />
      )}
    </div>
  );
}

/**
 * The refusals.
 *
 * There is no 404 case, and its absence is the point: this route takes no id, so
 * nothing about it can be absent. A 403 is permanent and offers no retry — the
 * API has already refused this caller (AUD-7, SEC-5).
 */
function AuditError({ status, onRetry }: { status: number; onRetry: () => void }) {
  return (
    <div className="flex flex-col items-start gap-3">
      <p role="alert" className="flex items-center gap-2 text-sm text-danger">
        <StatusDot tone="alert" />
        {status === 403 ? AUDIT_COPY.states.forbidden : AUDIT_COPY.states.error}
      </p>

      {status === 403 ? null : (
        <button
          type="button"
          onClick={onRetry}
          className="w-fit border px-3 py-1.5 text-xs font-medium transition-colors"
          style={neutralButtonStyle()}
        >
          {AUDIT_COPY.states.retry}
        </button>
      )}
    </div>
  );
}

/**
 * The person filter.
 *
 * **A closed `Select` now, not the free-text-plus-`datalist` it used to be.**
 * The reason it wasn't one — nothing the caller can call enumerates the actor
 * set, since `GET /users` returns this agency's `entra` members only, while
 * the interesting actors here also include counterpart individuals and
 * `Blue Partner` — still holds, and the tradeoff is real: this can only
 * filter to an address that has appeared on the *current* page of events,
 * where the old field could filter to any address regardless of whether it
 * was on screen. `AuditTable`'s per-row "Filter to…" button is still the
 * primary path either way, exactly as the old field's own doc said — this
 * is a convenience over addresses already on screen, not the only way in.
 * `Select`'s own leading option (`filters.actor.all`, "Everyone") replaces
 * the standalone Clear button, matching how `Show` and `Space` beside it
 * already clear via their own leading option rather than a second control.
 */
function ActorFilter({
  applied,
  events,
  onApply,
}: {
  applied: string | undefined;
  events: AuditEvent[];
  onApply: (value: string) => void;
}) {
  const suggestions = [...new Set(events.map((event) => event.actor.email))];
  const options: SelectOption[] = [
    { value: '', label: AUDIT_COPY.filters.actor.all },
    ...suggestions.map((email) => ({ value: email, label: email })),
  ];

  return (
    <label className="flex items-center gap-2 text-xs" style={{ color: token('bp-text-muted') }}>
      {AUDIT_COPY.filters.actor.label}
      <Select
        label={AUDIT_COPY.filters.actor.label}
        value={applied ?? ''}
        onChange={onApply}
        options={options}
      />
    </label>
  );
}

function AuditTable({
  events,
  onInspect,
  onFilterActor,
}: {
  events: AuditEvent[];
  onInspect: (event: AuditEvent) => void;
  onFilterActor: (email: string) => void;
}) {
  // Sorting off. The server pages **and** owns the order, and AUD-6a means the
  // order is not `occurredAt` descending — a client-side sort would silently
  // "fix" the one row the spec deliberately places elsewhere.
  const columns: DataTableColumn<AuditEvent>[] = [
    {
      id: 'occurredAt',
      header: AUDIT_COPY.columns.occurredAt,
      cell: (event) => (
        <div className="flex flex-col gap-0.5">
          {/* MetaRow owns the amber-for-time rule, so it is right without this
              component remembering it. */}
          <MetaRow
            time={formatUtcDateTime(event.occurredAt, AUDIT_COPY.dateLocale)}
            dateTime={event.occurredAt}
          />
          {event.dayBucketed ? (
            <span className="text-xs" style={{ color: token('bp-text-muted') }}>
              {AUDIT_COPY.dayBucketed.marker}
            </span>
          ) : null}
        </div>
      ),
    },
    {
      id: 'action',
      header: AUDIT_COPY.columns.action,
      cell: (event) => (
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: token('bp-text') }}>
            {actionLabel(event.action)}
          </span>
          <Badge tone={event.eventClass === 'counterpart' ? 'amber' : 'neutral'}>
            {AUDIT_COPY.classes[event.eventClass]}
          </Badge>
        </div>
      ),
    },
    {
      id: 'actor',
      header: AUDIT_COPY.columns.actor,
      cell: (event) => (
        <button
          type="button"
          // Named per row: several buttons carrying an address are
          // indistinguishable to a screen reader without one.
          aria-label={AUDIT_COPY.actorFilter(event.actor.email)}
          onClick={() => {
            onFilterActor(event.actor.email);
          }}
          className="text-left text-xs transition-colors hover:underline"
          style={{ color: token('bp-text-muted') }}
        >
          {event.actor.email}
          <span className="block" style={{ color: token('bp-text-muted') }}>
            {event.actor.agencyName}
          </span>
        </button>
      ),
    },
    {
      id: 'target',
      header: AUDIT_COPY.columns.target,
      cell: (event) => (
        <span className="text-xs" style={{ color: token('bp-text-muted') }}>
          {AUDIT_COPY.targets[event.target.type]}
        </span>
      ),
    },
    {
      id: 'space',
      header: AUDIT_COPY.columns.space,
      cell: (event) => (
        <span className="text-xs" style={{ color: token('bp-text-muted') }}>
          {event.space?.name ?? AUDIT_COPY.noSpace}
        </span>
      ),
    },
    {
      id: 'details',
      header: AUDIT_COPY.columns.details,
      align: 'end',
      cell: (event) => (
        <button
          type="button"
          aria-label={AUDIT_COPY.details.open(actionLabel(event.action))}
          onClick={() => {
            onInspect(event);
          }}
          className={BUTTON_CLASS}
          style={neutralButtonStyle()}
        >
          {AUDIT_COPY.details.label}
        </button>
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={events}
      rowKey={(event) => event.id}
      caption={AUDIT_COPY.states.tableCaption}
      emptyState={AUDIT_COPY.states.empty}
    />
  );
}

/**
 * One event in full.
 *
 * A drawer rather than a column, because `details` has a different shape per
 * action — `fileName`/`sizeBytes` on a download, an address on an invitation —
 * so a column would be either a JSON blob in a table cell or an unstable set of
 * columns. It is also where AUD-6a's sentence lives, on the one row that needs
 * it.
 */
function EventDetails({ event, onClose }: { event: AuditEvent; onClose: () => void }) {
  const payload = Object.entries(event.details ?? {});

  return (
    <DetailDrawer
      open
      onClose={onClose}
      title={actionLabel(event.action)}
      closeLabel={AUDIT_COPY.details.close}
    >
      <dl className="flex flex-col gap-3 text-sm">
        <Field label={AUDIT_COPY.details.fields.occurredAt} value={event.occurredAt} />
        <Field label={AUDIT_COPY.details.fields.actor} value={event.actor.email} />
        <Field label={AUDIT_COPY.details.fields.agency} value={event.actor.agencyName} />
        <Field
          label={AUDIT_COPY.details.fields.eventClass}
          value={AUDIT_COPY.classes[event.eventClass]}
        />
        <Field
          label={AUDIT_COPY.details.fields.target}
          value={`${AUDIT_COPY.targets[event.target.type]} · ${event.target.id}`}
        />
        {event.space === undefined ? null : (
          <Field label={AUDIT_COPY.details.fields.space} value={event.space.name} />
        )}
        <Field label={AUDIT_COPY.details.fields.eventId} value={event.id} />

        {payload.length === 0 ? (
          <p style={{ color: token('bp-text-muted') }}>{AUDIT_COPY.details.none}</p>
        ) : (
          <div className="flex flex-col gap-2">
            <dt className="text-xs" style={{ color: token('bp-text-muted') }}>
              {AUDIT_COPY.details.fields.payload}
            </dt>
            {payload.map(([name, value]) => (
              <dd key={name} className="text-xs" style={{ color: token('bp-text') }}>
                {name}
                {': '}
                {String(value)}
              </dd>
            ))}
          </div>
        )}

        {event.dayBucketed ? (
          <p className="text-xs" style={{ color: token('bp-text-muted') }}>
            {AUDIT_COPY.dayBucketed.explanation}
          </p>
        ) : null}
      </dl>
    </DetailDrawer>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs" style={{ color: token('bp-text-muted') }}>
        {label}
      </dt>
      <dd style={{ color: token('bp-text') }}>{value}</dd>
    </div>
  );
}
